// WP-C2.3 (CW-1): date + time + IANA timeZone <-> UTC ISO instant conversion for
// CalendarEventForm — pure Intl, no timezone-database dependency added to apps/web. Uses the
// standard "guess as UTC, measure the same instant in the target zone, correct by the
// difference" technique: a single correction is exact for every ordinary case (the only
// failure mode — a wall-clock time that's skipped or repeated by a DST transition in THAT
// zone, in THAT single minute — is the same edge case every other calendar UI silently picks
// one side of, not something worth a library for).
interface ZonedParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

function formatPartsInZone(date: Date, timeZone: string, withSeconds: boolean): ZonedParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  });
  // A raw Record<string,string> loses its non-undefined guarantee under this repo's
  // noUncheckedIndexedAccess — every field is filled in below from Intl's own fixed part list
  // for these exact options, so a concrete ZonedParts (with a '00' placeholder for the seconds
  // this function was asked to omit) is both accurate and lint-clean.
  const parts: ZonedParts = {
    year: '0000',
    month: '01',
    day: '01',
    hour: '00',
    minute: '00',
    second: '00',
  };
  for (const part of dtf.formatToParts(date)) {
    if (
      part.type === 'year' ||
      part.type === 'month' ||
      part.type === 'day' ||
      part.type === 'minute'
    )
      parts[part.type] = part.value;
    // Intl's hour12:false can format midnight as "24" in some engines — normalize to "00" so
    // the reconstructed ISO string below stays a valid calendar time.
    if (part.type === 'hour') parts.hour = part.value === '24' ? '00' : part.value;
    if (part.type === 'second') parts.second = part.value;
  }
  return parts;
}

/** Is `timeZone` a recognizable IANA zone name? Used to reject a typo before it silently falls
 * back to UTC everywhere below (Intl.DateTimeFormat itself throws on an unknown zone). */
export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** `dateStr` "yyyy-mm-dd", `timeStr` "HH:mm" — both wall-clock in `timeZone`. Returns the UTC
 * ISO instant, or null for invalid/incomplete input. */
export function zonedDateTimeToUtcIso(
  dateStr: string,
  timeStr: string,
  timeZone: string,
): string | null {
  if (dateStr.trim() === '' || timeStr.trim() === '' || !isValidTimeZone(timeZone)) return null;
  const guessUtc = new Date(`${dateStr}T${timeStr}:00.000Z`);
  if (Number.isNaN(guessUtc.getTime())) return null;

  const parts = formatPartsInZone(guessUtc, timeZone, true);
  const zonedAsUtc = new Date(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}.000Z`,
  );
  const offsetMs = guessUtc.getTime() - zonedAsUtc.getTime();
  return new Date(guessUtc.getTime() + offsetMs).toISOString();
}

/** Inverse direction, for pre-filling the edit form: an existing UTC ISO instant -> the
 * date/time strings a person in `timeZone` would read on their wall clock. */
export function utcIsoToZonedDateAndTime(
  iso: string,
  timeZone: string,
): { date: string; time: string } {
  const d = new Date(iso);
  const zone = isValidTimeZone(timeZone) ? timeZone : 'UTC';
  const parts = formatPartsInZone(d, zone, false);
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/** A short, curated fallback for engines without `Intl.supportedValuesOf` (older browsers,
 * some test runners) — `Intl.supportedValuesOf('timeZone')` is preferred when present. */
export const COMMON_TIME_ZONES = [
  'UTC',
  'Asia/Karachi',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
] as const;

export function listTimeZones(): string[] {
  try {
    if (typeof Intl.supportedValuesOf === 'function') return Intl.supportedValuesOf('timeZone');
  } catch {
    /* fall through to the curated list */
  }
  return [...COMMON_TIME_ZONES];
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}
